import React, { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  TextField,
  Grid,
  Typography,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import StyledDialogActions from '../../theme/dialogActionStyles';
// import BRDApproved from '../form-elements/brdapproved.component';
import Platforms from '../form-elements/platforms.component';
import Channels from '../form-elements/channels.component';
import Datepicker from '../form-elements/datepicker.component';
import {
  createNewAppVersion,
  updateVersionAppById,
  viewVersionAppById
} from '../../services/apps.service';
// import { BRD_APPROVED_LIST } from '../../config/const.config';
import PLATFORMS_LIST from '../../config/platforms.config';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const AddEditAppVersion = ({ title, dataId, onCancel, onSuccess }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      appId: null,
      name: null,
      version: null,
      brdVersion: null,
      bundleId: null,
      platform: null,
      forceUpgrade: null,
      storeUrl: null,
      notes: null,
      submittedAt: null,
      liveAt: null,
    },
  });

  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const saveNewData = (payload) => {
    createNewAppVersion(payload)
      .then(() => {
        onSuccess('New app version created successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateExistingData = (payload) => {
    updateVersionAppById(dataId, payload)
      .then(() => {
        onSuccess('App version details updated successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    const payload = {
      appId: data.name.id,
      name: data.name.title,
      version: data.version || null,
      brdVersion: data.brdVersion,
      brdApproved: data?.brdApproved?.id !== undefined ? Number(data.brdApproved.id === 1) : Number(!!data?.brdApproved),
      bundleId: data.bundleId,
      platform: data.platform.id || null,
      forceUpgrade: data?.forceUpgrade?.id !== undefined ? Number(data.forceUpgrade.id === 1) : Number(!!data?.forceUpgrade),
      storeUrl: data.storeUrl,
      notes: data.notes || null,
      submittedAt: data.submittedAt,
      liveAt: data.liveAt,
    };
    if (dataId) {
      updateExistingData(payload);
    } else {
      saveNewData(payload);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
      return;
    }

    viewVersionAppById(dataId)
      .then((res) => {
        // Fill form values
        setValue('name', {'id':res?.data?.appId, 'title':res?.data?.name} || null);
        setValue('version', res.data?.version || null);
        setValue('brdVersion', res.data?.brdVersion || null);
        setValue('forceUpgrade', res.data?.forceUpgrade || false);
        setValue('brdApproved', res.data?.brdApproved || false);
        setValue(
          'platform',
          PLATFORMS_LIST.filter((item) => item.id === res.data?.platform)?.[0] ||
            null
        );
        setValue('bundleId', res.data?.bundleId || null);
        setValue('submittedAt', res.data?.submittedAt || null);
        setValue('liveAt', res.data?.liveAt || null);
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      onClose={() => onCancel()}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="add-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        {loading && <Loading />}

        {!loading && (
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <Channels
                      id="name"
                      name="name"
                      label="App Name *"
                      defaultValue={value}
                      multiple={false}
                      onChange={onChange}
                      sx={{ width: '100%' }}
                      error={errors?.name?.message || ''}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="version"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(15),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="App Version *"
                      type="text"
                      error={!!errors.version}
                      helperText={errors?.version?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="brdVersion"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(30),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="BRD Version *"
                      type="text"
                      error={!!errors.brdVersion}
                      helperText={errors?.brdVersion?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              {/* <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="brdApproved"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <BRDApproved
                      id="brdApproved"
                      name="brdApproved"
                      label="BRD Approved?"
                      defaultValue={value}
                      multiple={false}
                      onChange={onChange}
                      sx={{ width: '100%' }}
                      error={errors?.brdApproved?.message || ''}
                    />
                  )}
                />
              </Grid> */}
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="brdApproved"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          aria-label="brdApproved"
                          color="primary"
                          checked={value}
                          onChange={onChange}
                        />
                      }
                      label="BRD Approved?"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="forceUpgrade"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          aria-label="forceUpgrade"
                          color="primary"
                          checked={value}
                          onChange={onChange}
                        />
                      }
                      label="Force Upgrade?"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="platform"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <Platforms
                      id="platform"
                      name="platform"
                      label="Select Platforms *"
                      defaultValue={value}
                      multiple={false}
                      onChange={onChange}
                      sx={{ width: '100%' }}
                      error={errors?.platform?.message || ''}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="bundleId"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(30),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Bundle ID *"
                      type="text"
                      error={!!errors.bundleId}
                      helperText={errors?.bundleId?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="submittedAt"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Datepicker
                      id="submittedAt"
                      name="submittedAt"
                      label="Submitted Date"
                      defaultValue={value}
                      multiple={false}
                      onChange={onChange}
                      sx={{ width: '100%' }}
                      error={errors?.submittedAt?.message || ''}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="liveAt"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Datepicker
                      id="liveAt"
                      name="liveAt"
                      label="App Live Date"
                      defaultValue={value}
                      multiple={false}
                      onChange={onChange}
                      sx={{ width: '100%' }}
                      error={errors?.liveAt?.message || ''}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>

      <StyledDialogActions>
        <Grid>
          <Button
            type="submit"
            form="add-edit-form"
            color="primary"
            variant="contained"
            disabled={loading || formSubmitted}
            sx={{ marginRight: 2 }}
          >
            Save
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={onCancel}
            disabled={formSubmitted}
          >
            Cancel
          </Button>
        </Grid>
        <Grid>
          <Typography variant="body2" color="secondary">
            * indicates a required field
          </Typography>
        </Grid>
      </StyledDialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

AddEditAppVersion.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddEditAppVersion;
